import { useEffect, useState, useRef } from "react";
import { useMutationObserver } from "./useMutationObserver";

class FlowHelper {
	constructor(
		mainGroupContainer,
		overflowGroupContainer,
		children,
		getDocContainer
	) {
		this.mainGroupContainer = mainGroupContainer;
		this.overflowGroupContainer = overflowGroupContainer;
		this.children = children;
		this.getDocContainer = getDocContainer;

		this.flowChildrenAround = breakPoint => {
			this.getDocContainer(this.mainGroupContainer).innerHtml = "";
			this.getDocContainer(this.overflowGroupContainer).innerHtml = "";

			for (let i = 0; i < children.length; i++) {
				if (i < breakPoint) {
					getDocContainer(this.mainGroupContainer).appendChild(
						this.children[i]
					);
				} else {
					getDocContainer(this.overflowGroupContainer).appendChild(
						this.children[i]
					);
				}
			}
		};

		this.isGapAboveLastGroup = (breakPoint, maxBottom) => {
			const getFirstChild = () =>
				this.getDocContainer(this.mainGroupContainer).childNodes[
					breakPoint - 1
				];

			const isGapBelow = firstOverflowNode =>
				firstOverflowNode.getBoundingClientRect().bottom +
					window.pageYOffset <
				maxBottom;

			const firstOverflowNode = getFirstChild();

			if (firstOverflowNode) {
				return isGapBelow(firstOverflowNode);
			} else {
				this.flowChildrenAround(breakPoint);
				const firstOverflowNode = getFirstChild();
				if (firstOverflowNode) {
					return isGapBelow(firstOverflowNode);
				}
			}
			return false;
		};
	}
}

const useImageIntervalCheck = (
	mainGroupContainer,
	overflowGroupContainer,
	flowAroundContainer,
	setContainerAttributesUpdated
) => {
	const handleMut = () => {
		setContainerAttributesUpdated(prev => prev + 1);
	};

	useMutationObserver(mainGroupContainer, handleMut, {
		subtree: true,
		attributes: true,
	});

	useMutationObserver(overflowGroupContainer, handleMut, {
		subtree: true,
		attributes: true,
	});

	const [shouldCheckImage, setShouldCheckImage] = useState(true);
	const [checkingImage, setCheckingImage] = useState(false);
	const [intervalId, setIntervalId] = useState(-1);

	useEffect(() => {
		if (!shouldCheckImage && checkingImage) {
			clearInterval(intervalId);
			setCheckingImage(false);
			setIntervalId(-1);
			return;
		}

		const checkImage = () => {
			if (flowAroundContainer.current) {
				const img = flowAroundContainer.current.querySelector("img");
				if (img && img.complete && img.naturalHeight !== 0) {
					setContainerAttributesUpdated(prev => prev + 1);
					return true;
				}
			}
			return false;
		};

		if (intervalId === -1) {
			setIntervalId(
				setInterval(() => {
					if (checkImage()) {
						setShouldCheckImage(false);
					}
				}, 300)
			);
		}

		return () => (intervalId !== -1 ? clearInterval(intervalId) : null);
	}, [shouldCheckImage, checkingImage, intervalId]);
};

export const useFlowAround = (
	{
		flowAroundContainer,
		mainGroupContainer,
		overflowGroupContainer,
		subGroupClass,
		groupContainerClass,
	},
	flowAroundContainerState,
	mobileBreakpoint = 1200
) => {
	const [currentWidth, setCurrentWidth] = useState(0);
	const [
		containerAttributesUpdated,
		setContainerAttributesUpdated,
	] = useState(1);

	const getDocContainer = container =>
		container.current.querySelectorAll(`.${groupContainerClass}`)[0];

	useEffect(() => {
		let breakPoint;

		const groupContainer = getDocContainer(mainGroupContainer);
		const groupOverflowContainer = getDocContainer(overflowGroupContainer);

		const gc = Array.prototype.slice.call(
			groupContainer.querySelectorAll(`.${subGroupClass}`)
		);
		const gcOverflow = Array.prototype.slice.call(
			groupOverflowContainer.querySelectorAll(`.${subGroupClass}`)
		);
		const nodesInMainContainer = gc.length;
		const children = gc.concat(gcOverflow);

		const flowHelper = new FlowHelper(
			mainGroupContainer,
			overflowGroupContainer,
			children,
			getDocContainer
		);

		if (currentWidth < mobileBreakpoint) {
			// flow into bottom container on mobile
			flowHelper.flowChildrenAround(0);
			return;
		}

		if (nodesInMainContainer === 0) {
			// flow back into main container if nothing there
			// happens when switching from mobile to desktop
			flowHelper.flowChildrenAround(children.length);
		}

		const imgBottom =
			flowAroundContainer.current.getBoundingClientRect().bottom +
			window.pageYOffset;

		for (breakPoint = 0; breakPoint < children.length; breakPoint++) {
			const clientRect = children[breakPoint].getBoundingClientRect();
			const top = clientRect.top + window.pageYOffset;

			if (top > imgBottom) {
				break;
			}
		}

		while (flowHelper.isGapAboveLastGroup(breakPoint, imgBottom)) {
			breakPoint++;
		}

		flowHelper.flowChildrenAround(breakPoint);
	}, [
		currentWidth,
		flowAroundContainerState,
		mobileBreakpoint,
		containerAttributesUpdated,
	]);

	useImageIntervalCheck(
		mainGroupContainer,
		overflowGroupContainer,
		flowAroundContainer,
		setContainerAttributesUpdated
	);

	useEffect(() => {
		setCurrentWidth(window.innerWidth);

		const updateOverflowOnResize = () => {
			if (Math.abs(window.innerWidth - currentWidth) > 20) {
				setCurrentWidth(window.innerWidth);
			}
		};

		const updateLayoutOnReadyStateChange = () => {
			if (document.readyState === "complete") {
				setContainerAttributesUpdated(prev => prev + 1);
			}
		};

		window.addEventListener("resize", updateOverflowOnResize);
		document.addEventListener(
			"readystatechange",
			updateLayoutOnReadyStateChange
		);

		setContainerAttributesUpdated(prev => prev + 1);

		return () => {
			window.removeEventListener("resize", updateOverflowOnResize);
			document.removeEventListener(
				"readystatechange",
				updateLayoutOnReadyStateChange
			);
		};
	}, []);
};
